import React, { useEffect, useMemo, useState } from "react";
import { Search, Trash2, AlertTriangle, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { rupiah } from "@/lib/format";
import { Modal, Button, PhotoUpload } from "@/components/common";

const MATERIAL_CATS = ["fresh", "artificial", "raw_material", "packaging", "accessories", "supplies", "other"];

export default function MaterialUsageModal({ type, order, product, editUsage, onClose, onSaved }) {
  const { branch } = useAuth();
  const isEdit = !!editUsage;
  const usageType = editUsage?.type || type;
  const branchId = order?.branch_id || editUsage?.branch_id || branch?.id;
  const [catalog, setCatalog] = useState([]);
  const [rows, setRows] = useState(() => (editUsage?.lines || []).map((l) => ({ material_id: l.material_id, qty: l.qty })));
  const [search, setSearch] = useState("");
  const [qcPhoto, setQcPhoto] = useState(editUsage?.qc_photo_file_id || null);
  const [notes, setNotes] = useState(editUsage?.notes || "");
  const [displayName, setDisplayName] = useState("");
  const [displayPrice, setDisplayPrice] = useState("");
  const [producedQty, setProducedQty] = useState(editUsage?.produced_qty ? String(editUsage.produced_qty) : "1");
  const [displayPhoto, setDisplayPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pin, setPin] = useState("");
  const [needPin, setNeedPin] = useState(false);
  const [shortages, setShortages] = useState([]);

  useEffect(() => {
    if (!branchId) return;
    api.get("/inventory", { branch_id: branchId })
      .then((d) => setCatalog((d || []).filter((r) => r.item_type === "material" && MATERIAL_CATS.includes(r.category))))
      .catch(() => setCatalog([]));
  }, [branchId]);

  const byId = useMemo(() => Object.fromEntries(catalog.map((m) => [m.item_id, m])), [catalog]);
  const chosen = new Set(rows.map((r) => r.material_id));
  const results = useMemo(() => {
    const q = search.trim().toLowerCase();
    return catalog.filter((m) => !chosen.has(m.item_id) && (!q || `${m.name} ${m.sku || ""}`.toLowerCase().includes(q))).slice(0, 8);
  }, [catalog, search, rows]);

  const addRow = (m) => { setRows((r) => [...r, { material_id: m.item_id, qty: 1 }]); setSearch(""); };
  const setQty = (id, v) => setRows((r) => r.map((x) => (x.material_id === id ? { ...x, qty: v } : x)));
  const removeRow = (id) => setRows((r) => r.filter((x) => x.material_id !== id));

  const total = rows.reduce((s, r) => s + (Number(r.qty) || 0) * (byId[r.material_id]?.cost_price || 0), 0);

  const submit = async () => {
    if (!branchId) return toast.error("Cabang belum dipilih");
    if (!rows.length) return toast.error("Pilih minimal satu bahan");
    if (rows.some((r) => !(Number(r.qty) > 0))) return toast.error("Qty tiap bahan harus lebih dari 0");
    if (usageType === "display" && !isEdit && !product && !displayName.trim()) return toast.error("Nama produk display wajib diisi");
    const payload = {
      type: usageType, branch_id: branchId,
      lines: rows.map((r) => ({ material_id: r.material_id, qty: Number(r.qty) })),
      qc_photo_file_id: qcPhoto, notes,
      ...(needPin && pin ? { manager_pin: pin } : {}),
    };
    if (usageType === "order") payload.order_id = order?.id || editUsage?.order_id;
    if (usageType === "display") {
      if (product) payload.product_id = product.id;
      else if (!isEdit) { payload.display_name = displayName.trim(); payload.display_price = Number(displayPrice) || 0; payload.display_photo_file_id = displayPhoto; }
      payload.produced_qty = Number(producedQty) || 1;
    }
    setBusy(true);
    try {
      if (isEdit) await api.put(`/production/usage/${editUsage.id}`, payload);
      else await api.post("/production/usage", payload);
      toast.success(isEdit ? "Pemakaian bahan diperbarui" : usageType === "order" ? "Rangkaian selesai → Siap QC" : "Produk display berhasil dibuat & masuk stok");
      onSaved?.();
      onClose();
    } catch (e) {
      if (e.status === 409 && e.data?.detail?.shortages) {
        setShortages(e.data.detail.shortages); setNeedPin(true);
        toast.error("Stok bahan kurang — perlu PIN Manager untuk lanjut");
      } else if (e.status === 400 && e.data?.detail?.shortages) {
        setShortages(e.data.detail.shortages);
        toast.error("Stok bahan tidak cukup");
      } else {
        toast.error(e.data?.detail?.message || e.message || "Gagal menyimpan");
      }
    } finally { setBusy(false); }
  };

  const title = isEdit ? `Edit Pemakaian — ${editUsage.usage_no}` : usageType === "order" ? `Catat Bahan — ${order?.order_no}` : product ? `Tambah Stok Display — ${product.name}` : "Buat Produk Display Toko Baru";

  return (
    <Modal open onClose={onClose} title={title} maxWidth="max-w-2xl">
      <div className="space-y-4">
        {usageType === "display" && !product && !isEdit && (
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><PhotoUpload value={displayPhoto} onChange={setDisplayPhoto} label="Foto Produk Display" tall testid="usage-display-photo" /></div>
            <label className="block"><span className="text-sm font-medium text-slate-600 mb-1 block">Nama Produk Display</span>
              <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="cth: Buket Mawar Ready Stock" className="w-full h-10 px-3 rounded-xl border border-slate-300 text-sm" data-testid="usage-display-name" /></label>
            <label className="block"><span className="text-sm font-medium text-slate-600 mb-1 block">Harga Jual (Rp)</span>
              <input type="number" value={displayPrice} onChange={(e) => setDisplayPrice(e.target.value)} className="w-full h-10 px-3 rounded-xl border border-slate-300 text-sm" data-testid="usage-display-price" /></label>
          </div>
        )}
        {usageType === "display" && (
          <label className="block"><span className="text-sm font-medium text-slate-600 mb-1 block">Jumlah unit dibuat</span>
            <input type="number" min="1" value={producedQty} onChange={(e) => setProducedQty(e.target.value)} className="w-32 h-10 px-3 rounded-xl border border-slate-300 text-sm" data-testid="usage-produced-qty" /></label>
        )}

        {/* SEARCHABLE MATERIAL PICKER */}
        <div>
          <span className="text-sm font-medium text-slate-600 mb-1 block">Cari & pilih bahan baku / perlengkapan</span>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Ketik nama bahan (cth: Mawar)..." className="w-full h-11 pl-9 pr-3 rounded-xl border border-slate-300 text-sm" data-testid="usage-search" />
            {search && results.length > 0 && (
              <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-56 overflow-y-auto" data-testid="usage-search-results">
                {results.map((m) => (
                  <button key={m.item_id} onClick={() => addRow(m)} className="w-full flex items-center justify-between gap-2 px-3 py-2 hover:bg-slate-50 text-left text-sm" data-testid={`usage-opt-${m.item_id}`}>
                    <span className="text-slate-700">{m.name}</span>
                    <span className={`text-xs ${m.qty_available > 0 ? "text-slate-400" : "text-rose-500"}`}>Stok: {(+m.qty_available).toLocaleString("id-ID")} {m.unit}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* DYNAMIC ROWS */}
        {rows.length > 0 && (
          <div className="border border-slate-200 rounded-xl overflow-hidden" data-testid="usage-rows">
            <div className="grid grid-cols-[1fr_90px_60px_110px_36px] gap-2 px-3 py-2 bg-slate-50 text-xs font-medium text-slate-500">
              <span>Bahan</span><span className="text-center">Qty</span><span>Satuan</span><span className="text-right">Subtotal</span><span></span>
            </div>
            {rows.map((r) => {
              const m = byId[r.material_id] || {};
              const over = (Number(r.qty) || 0) > (m.qty_available || 0);
              const sub = (Number(r.qty) || 0) * (m.cost_price || 0);
              return (
                <div key={r.material_id} className="grid grid-cols-[1fr_90px_60px_110px_36px] gap-2 px-3 py-2 items-center border-t border-slate-100" data-testid={`usage-row-${r.material_id}`}>
                  <div className="min-w-0">
                    <div className="text-sm text-slate-800 truncate">{m.name}</div>
                    <div className={`text-[11px] flex items-center gap-1 ${over ? "text-rose-600 font-medium" : "text-slate-400"}`}>
                      {over && <AlertTriangle size={11} />} Stok: {(+(m.qty_available || 0)).toLocaleString("id-ID")} {m.unit}
                    </div>
                  </div>
                  <input type="number" step="0.1" min="0" value={r.qty} onChange={(e) => setQty(r.material_id, e.target.value)} className={`h-9 px-2 rounded-lg border text-sm text-center ${over ? "border-rose-400 bg-rose-50" : "border-slate-300"}`} data-testid={`usage-qty-${r.material_id}`} />
                  <span className="text-xs text-slate-500">{m.unit}</span>
                  <span className="text-right text-sm font-medium text-slate-700" data-testid={`usage-sub-${r.material_id}`}>{rupiah(sub)}</span>
                  <button onClick={() => removeRow(r.material_id)} className="text-rose-500 flex items-center justify-center" data-testid={`usage-remove-${r.material_id}`}><Trash2 size={15} /></button>
                </div>
              );
            })}
            <div className="flex items-center justify-between px-3 py-2.5 bg-slate-50 border-t border-slate-200">
              <span className="text-sm font-medium text-slate-600">Total Harga Modal</span>
              <span className="text-lg font-bold text-primary" data-testid="usage-total">{rupiah(total)}</span>
            </div>
          </div>
        )}

        {usageType === "order" && <PhotoUpload value={qcPhoto} onChange={setQcPhoto} label="Foto Hasil Rangkaian (bukti QC)" tall testid="usage-qc-photo" />}
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Catatan (opsional)" rows={2} className="w-full px-3 py-2 rounded-xl border border-slate-300 text-sm" data-testid="usage-notes" />

        {shortages.length > 0 && (
          <div className="rounded-xl border border-rose-300 bg-rose-50 p-3 text-sm" data-testid="usage-shortages">
            <div className="font-medium text-rose-700 flex items-center gap-1"><AlertTriangle size={14} /> Stok bahan tidak cukup</div>
            {shortages.map((sh) => <div key={sh.item_id} className="text-rose-600 text-xs mt-1">• {sh.name}: butuh {sh.needed}, tersedia {sh.available}</div>)}
            {needPin && <input value={pin} onChange={(e) => setPin(e.target.value)} type="password" inputMode="numeric" placeholder="PIN Manager untuk lanjut" className="mt-2 w-full h-10 px-3 rounded-xl border border-rose-300 text-sm" data-testid="usage-pin" />}
          </div>
        )}

        <Button className="w-full" size="lg" onClick={submit} disabled={busy} data-testid="usage-submit">
          {busy ? "Menyimpan..." : isEdit ? "Simpan Perubahan" : usageType === "order" ? "Selesaikan Rangkaian → Siap QC" : "Simpan Produk Display"}
        </Button>
      </div>
    </Modal>
  );
}
